import { useEffect, useState } from 'react'
import { usePrefersReducedMotion } from './usePrefersReducedMotion'

interface Options {
  typeSpeed?: number
  deleteSpeed?: number
  /** Pause once a phrase is fully typed. */
  holdTime?: number
  loop?: boolean
}

/**
 * Cycles through `phrases`, typing and deleting one character at a time.
 * Replaces the typed.js CDN script the old site loaded.
 *
 * When the visitor prefers reduced motion, the first phrase is shown at once
 * and nothing animates.
 */
export function useTypewriter(
  phrases: readonly string[],
  { typeSpeed = 70, deleteSpeed = 40, holdTime = 1600, loop = true }: Options = {},
): { text: string; isDone: boolean } {
  const reduceMotion = usePrefersReducedMotion()
  const [index, setIndex] = useState(0)
  const [length, setLength] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDone, setIsDone] = useState(false)

  const phrase = phrases[index % phrases.length] ?? ''

  useEffect(() => {
    if (reduceMotion || phrases.length === 0 || isDone) return

    // Finished typing this phrase: hold, then either stop or start deleting.
    if (!isDeleting && length === phrase.length) {
      if (!loop && index === phrases.length - 1) {
        setIsDone(true)
        return
      }
      const timer = setTimeout(() => setIsDeleting(true), holdTime)
      return () => clearTimeout(timer)
    }

    // Finished deleting: advance to the next phrase.
    if (isDeleting && length === 0) {
      setIsDeleting(false)
      setIndex((i) => (i + 1) % phrases.length)
      return
    }

    const timer = setTimeout(
      () => setLength((n) => n + (isDeleting ? -1 : 1)),
      isDeleting ? deleteSpeed : typeSpeed,
    )
    return () => clearTimeout(timer)
  }, [
    deleteSpeed,
    holdTime,
    index,
    isDeleting,
    isDone,
    length,
    loop,
    phrase.length,
    phrases.length,
    reduceMotion,
    typeSpeed,
  ])

  if (reduceMotion) return { text: phrases[0] ?? '', isDone: true }
  return { text: phrase.slice(0, length), isDone }
}
